'use client';

import React, {
  KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';
import { faEllipsisV } from '@fortawesome/pro-regular-svg-icons/faEllipsisV';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import useModal from '@osmosis/tailwind-ui/hooks/useModal';
import useClickOutside from '@osmosis/tailwind-ui/hooks/useClickOutside';
import { slideVertical } from '@osmosis/tailwind-ui/styles/animation';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { text5, textDarkBlue } from '@osmosis/tailwind-ui/styles/text';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';
import {
  borderColor300,
  borderColorBlue700,
} from '@osmosis/tailwind-ui/styles/border';
import { isVisibleForProgram } from '@osmosis/shared/types/notes';
import { useUserContext } from '../../../context/UserContext';

export type LearnTab = {
  id: string;
  label: string;
  panel: React.ReactNode;
  /**
   * Program aliases (e.g. 'DO') allowed to see this tab. Empty/absent means
   * visible to everyone. Filtered client-side against the hydrated user because
   * tabs are built server-side before the user is known.
   */
  requiredPrograms?: string[];
};

type Props = {
  tabs: LearnTab[];
  defaultTabId?: string;
  'aria-label'?: string;
};

// Horizontal gap between tabs (matches `gap-2` => 0.5rem).
const TAB_GAP_PX = 8;
// Horizontal padding of the tablist (matches `px-2` => 0.5rem each side).
const TABLIST_PADDING_PX = 16;
// Width reserved for the overflow (kebab) trigger when tabs don't all fit.
const OVERFLOW_TRIGGER_PX = 44;

const TAB_BASE_CLASSES =
  'px-4 py-2 text-lg !text-nowrap rounded-none border-b-4 border-transparent transition-colors duration-200 ease-in-out';

/**
 * Accessible tab container for the Learn page.
 *
 * When the tab row does not fit its container, the tabs that overflow are moved
 * into a kebab (⋮) pop-up menu instead of horizontally scrolling. Selecting a
 * tab from the menu promotes it into the last visible slot so the active tab is
 * always shown in the row.
 */
const LearnTabs = ({
  tabs: rawTabs,
  defaultTabId,
  'aria-label': ariaLabel,
}: Props) => {
  const intl = useIntl();
  const { user } = useUserContext();

  // Drop tabs the current user's program isn't allowed to see (e.g. OMM notes
  // are DO-only). Fail closed: gated tabs stay hidden until the user hydrates.
  const tabs = useMemo(
    () =>
      rawTabs.filter((tab) =>
        isVisibleForProgram(tab.requiredPrograms, user?.prime?.product),
      ),
    [rawTabs, user],
  );

  const initialId =
    defaultTabId && tabs.some((tab) => tab.id === defaultTabId)
      ? defaultTabId
      : tabs[0]?.id;

  const [activeId, setActiveId] = useState<string | undefined>(initialId);
  const [visibleCount, setVisibleCount] = useState(tabs.length);

  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const measureRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const overflowWrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuItemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // When an overflow tab is promoted via the menu, focus it after it renders.
  const focusPromotedRef = useRef(false);
  // Whether the menu should open upward (when there isn't room below).
  const [openUp, setOpenUp] = useState(false);

  const {
    isVisible: isMenuOpen,
    hide: hideMenu,
    toggle: toggleMenu,
  } = useModal();

  useClickOutside(overflowWrapperRef, () => {
    if (isMenuOpen) hideMenu();
  });

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const compute = () => {
      const containerWidth = container.clientWidth;
      const widths = measureRefs.current
        .slice(0, tabs.length)
        .map((el) => el?.offsetWidth ?? 0);

      // No layout available (SSR / jsdom, nothing measured yet): show every
      // tab. We require a real container width AND at least one measured tab.
      if (!containerWidth || widths.every((w) => w === 0)) {
        setVisibleCount(tabs.length);
        return;
      }

      const available = containerWidth - TABLIST_PADDING_PX;
      const totalAll =
        widths.reduce((sum, w) => sum + w, 0) +
        TAB_GAP_PX * Math.max(0, tabs.length - 1);

      if (totalAll <= available) {
        setVisibleCount(tabs.length);
        return;
      }

      const usable = available - OVERFLOW_TRIGGER_PX;
      let used = 0;
      let count = 0;
      for (let i = 0; i < tabs.length; i += 1) {
        const next = used + (count > 0 ? TAB_GAP_PX : 0) + widths[i];
        if (next <= usable) {
          used = next;
          count += 1;
        } else {
          break;
        }
      }

      setVisibleCount(Math.max(1, count));
    };

    compute();

    const observer = new ResizeObserver(compute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [tabs]);

  const { visibleTabs, overflowTabs } = useMemo(() => {
    if (visibleCount >= tabs.length) {
      return { visibleTabs: tabs, overflowTabs: [] as LearnTab[] };
    }

    let visible = tabs.slice(0, visibleCount);
    const activeIndex = tabs.findIndex((tab) => tab.id === activeId);

    // Promote the active tab into the last visible slot when it would overflow.
    if (activeIndex >= visibleCount) {
      visible = [
        ...tabs.slice(0, Math.max(0, visibleCount - 1)),
        tabs[activeIndex],
      ];
    }

    const visibleIds = new Set(visible.map((tab) => tab.id));
    const overflow = tabs.filter((tab) => !visibleIds.has(tab.id));
    return { visibleTabs: visible, overflowTabs: overflow };
  }, [tabs, visibleCount, activeId]);

  // Close the menu if the overflow set disappears (e.g. the container grew).
  useEffect(() => {
    if (!overflowTabs.length && isMenuOpen) hideMenu();
  }, [overflowTabs.length, isMenuOpen, hideMenu]);

  // When the menu opens: decide whether to flip upward (insufficient space
  // below) and move focus into the menu.
  useLayoutEffect(() => {
    if (!isMenuOpen) return;
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (trigger && menu) {
      const spaceBelow =
        window.innerHeight - trigger.getBoundingClientRect().bottom;
      setOpenUp(menu.offsetHeight > spaceBelow);
    }
  }, [isMenuOpen]);

  // Move focus into the menu when it opens.
  useEffect(() => {
    if (isMenuOpen) menuItemRefs.current[0]?.focus();
  }, [isMenuOpen]);

  // After promoting an overflow tab via the menu, focus it in the visible row.
  useEffect(() => {
    if (!focusPromotedRef.current) return;
    focusPromotedRef.current = false;
    const index = visibleTabs.findIndex((tab) => tab.id === activeId);
    if (index >= 0) tabRefs.current[index]?.focus();
  }, [visibleTabs, activeId]);

  if (!tabs.length) return null;

  // Roving tabindex: move focus (and selection) between visible tabs so the
  // whole tablist is a single Tab stop. Wraps around at both ends.
  const focusTab = (index: number) => {
    const count = visibleTabs.length;
    if (!count) return;
    const nextIndex = (index + count) % count;
    setActiveId(visibleTabs[nextIndex].id);
    tabRefs.current[nextIndex]?.focus();
  };

  const onTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        focusTab(index + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        focusTab(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusTab(0);
        break;
      case 'End':
        event.preventDefault();
        focusTab(visibleTabs.length - 1);
        break;
      default:
        break;
    }
  };

  const focusMenuItem = (index: number) => {
    const count = overflowTabs.length;
    if (!count) return;
    const nextIndex = (index + count) % count;
    menuItemRefs.current[nextIndex]?.focus();
  };

  const selectOverflowTab = (id: string) => {
    setActiveId(id);
    focusPromotedRef.current = true;
    hideMenu();
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !isMenuOpen) {
      event.preventDefault();
      toggleMenu();
    }
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      hideMenu();
      triggerRef.current?.focus();
    }
  };

  const onMenuItemKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusMenuItem(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusMenuItem(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusMenuItem(0);
        break;
      case 'End':
        event.preventDefault();
        focusMenuItem(overflowTabs.length - 1);
        break;
      default:
        break;
    }
  };

  const tablistLabel =
    ariaLabel ??
    intl.formatMessage({
      id: 'learn.tabs.aria_label',
      defaultMessage: 'Learn content sections',
    });

  const renderTabButton = (tab: LearnTab, index: number) => {
    const isActive = tab.id === activeId;
    return (
      <Button
        key={tab.id}
        ref={(el) => {
          tabRefs.current[index] = el as HTMLButtonElement | null;
        }}
        type="button"
        variant="transparent"
        color="transparent"
        role="tab"
        id={`learn-tab-${tab.id}`}
        aria-selected={isActive}
        aria-controls={`learn-tabpanel-${tab.id}`}
        tabIndex={isActive ? 0 : -1}
        onClick={() => setActiveId(tab.id)}
        onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) =>
          onTabKeyDown(event, index)
        }
        data-testid={`learn-tab-${tab.id}`}
        className={cn(
          TAB_BASE_CLASSES,
          isActive
            ? cn('font-bold', textDarkBlue, borderColorBlue700)
            : cn(
                'font-semibold',
                text5,
                'hover:text-blue-700 hover:border-blue-700',
              ),
        )}
      >
        {tab.label}
      </Button>
    );
  };

  return (
    <Box
      data-testid="learn-tabs-wrapper"
      className={cn(bg0, borderColor300, 'border-1 rounded-lg mb-2')}
    >
      <Box ref={containerRef} className="relative border-b">
        {/* Hidden measuring row: always renders every tab at natural width so
            overflow can be recomputed when the container grows again. Wrapped in
            an overflow-hidden layer so its natural (possibly very wide) width
            cannot trigger page-level horizontal scrolling. offsetWidth of the
            spans is unaffected by the clipping. */}
        <Box
          aria-hidden="true"
          className="pointer-events-none invisible absolute inset-0 overflow-hidden"
        >
          <Flex
            data-testid="learn-tabs-measure"
            className="flex-nowrap gap-2 px-2"
          >
            {tabs.map((tab, index) => (
              <span
                key={tab.id}
                ref={(el) => {
                  measureRefs.current[index] = el;
                }}
                // Measured at `font-bold` (the widest weight a tab can take) so
                // a tab never overflows after becoming active/bold. Slightly
                // conservative: it may keep one extra tab in the menu.
                className={cn(TAB_BASE_CLASSES, 'font-bold inline-block')}
              >
                {tab.label}
              </span>
            ))}
          </Flex>
        </Box>

        <Flex
          role="tablist"
          aria-label={tablistLabel}
          data-testid="learn-tabs"
          className="flex-nowrap items-stretch gap-2 px-2 pt-1"
        >
          {visibleTabs.map((tab, index) => renderTabButton(tab, index))}

          {overflowTabs.length > 0 && (
            <Box
              ref={overflowWrapperRef}
              className="relative ml-auto flex items-center"
            >
              <Button
                ref={triggerRef}
                type="button"
                variant="transparent"
                color="transparent"
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                aria-label={intl.formatMessage({
                  id: 'learn.tabs.more',
                  defaultMessage: 'More sections',
                })}
                data-testid="learn-tabs-overflow-trigger"
                onClick={toggleMenu}
                onKeyDown={onTriggerKeyDown}
                className={cn(
                  'px-3 py-2 text-lg border-b-4 border-transparent hover:bg-blue-100 hover:text-darkest-blue-500',
                  text5,
                )}
              >
                <Icon icon={faEllipsisV} />
              </Button>

              <AnimatePresence>
                {isMenuOpen && (
                  <Box
                    className={cn(
                      'absolute right-0 z-20',
                      openUp ? 'bottom-full mb-1' : 'top-full mt-1',
                    )}
                    onKeyDown={onMenuKeyDown}
                  >
                    <motion.div
                      ref={menuRef}
                      data-testid="learn-tabs-overflow-menu"
                      role="menu"
                      aria-label={tablistLabel}
                      className={cn(
                        bg0,
                        'min-w-48 max-h-[80vh] overflow-y-auto rounded-md border-2 border-neutral-200 shadow-md p-1',
                      )}
                      {...slideVertical()}
                    >
                      {overflowTabs.map((tab, index) => {
                        const isActive = tab.id === activeId;
                        return (
                          <Button
                            key={tab.id}
                            ref={(el) => {
                              menuItemRefs.current[index] =
                                el as HTMLButtonElement | null;
                            }}
                            type="button"
                            variant="transparent"
                            color="transparent"
                            role="menuitem"
                            data-testid={`learn-tabs-overflow-item-${tab.id}`}
                            onClick={() => selectOverflowTab(tab.id)}
                            onKeyDown={(
                              event: KeyboardEvent<HTMLButtonElement>,
                            ) => onMenuItemKeyDown(event, index)}
                            className={cn(
                              'w-full justify-start px-4 py-2 text-left text-lg transition-colors rounded-md hover:bg-blue-100 hover:text-darkest-blue-500',
                              isActive
                                ? cn('font-bold', textDarkBlue)
                                : cn('font-semibold', text5),
                            )}
                          >
                            {tab.label}
                          </Button>
                        );
                      })}
                    </motion.div>
                  </Box>
                )}
              </AnimatePresence>
            </Box>
          )}
        </Flex>
      </Box>

      {tabs.map((tab) => (
        <Box
          key={tab.id}
          role="tabpanel"
          id={`learn-tabpanel-${tab.id}`}
          aria-labelledby={`learn-tab-${tab.id}`}
          tabIndex={0}
          data-testid={`learn-tabpanel-${tab.id}`}
          className={cn(
            tab.id !== activeId && 'hidden',
            '[&>div:first-child]:rounded-0 [&>div:first-child]:border-0 min-h-[40vh]',
          )}
        >
          {tab.panel}
        </Box>
      ))}
    </Box>
  );
};

export default LearnTabs;
